import { useMemo } from 'react';
import { Box } from '@mui/material';

import CalendarBodyRow from './CalendarBodyRow';
import { initializeCalendarData, prepareDocumentDataByWeek } from './utils';
import { DocumentMapEnum } from './constants';

import type { ICalendarData } from './HistoricalDocumentCalendar';
import type { Moment } from 'moment';

interface ICalendarBodyProps {
  loading: boolean;
  week: [Moment, Moment, Moment, Moment, Moment, Moment, Moment];
  data: ICalendarData;
}

function CalendarBody({ loading, data, week }: ICalendarBodyProps) {
  const [sun, mon, tue, wed, thu, fri, sat] = week;

  const [sunData, monData, tueData, wedData, thuData, friData, satData] = useMemo(() => {
    const sunData: ICalendarData = initializeCalendarData();
    const monData: ICalendarData = initializeCalendarData();
    const tueData: ICalendarData = initializeCalendarData();
    const wedData: ICalendarData = initializeCalendarData();
    const thuData: ICalendarData = initializeCalendarData();
    const friData: ICalendarData = initializeCalendarData();
    const satData: ICalendarData = initializeCalendarData();

    const assessmentData = prepareDocumentDataByWeek(week, data[DocumentMapEnum.ASSESSMENT]);
    sunData[DocumentMapEnum.ASSESSMENT] = assessmentData.sunData;
    monData[DocumentMapEnum.ASSESSMENT] = assessmentData.monData;
    tueData[DocumentMapEnum.ASSESSMENT] = assessmentData.tueData;
    wedData[DocumentMapEnum.ASSESSMENT] = assessmentData.wedData;
    thuData[DocumentMapEnum.ASSESSMENT] = assessmentData.thuData;
    friData[DocumentMapEnum.ASSESSMENT] = assessmentData.friData;
    satData[DocumentMapEnum.ASSESSMENT] = assessmentData.satData;

    const btSNData = prepareDocumentDataByWeek(week, data[DocumentMapEnum.BT_SN]);
    sunData[DocumentMapEnum.BT_SN] = btSNData.sunData;
    monData[DocumentMapEnum.BT_SN] = btSNData.monData;
    tueData[DocumentMapEnum.BT_SN] = btSNData.tueData;
    wedData[DocumentMapEnum.BT_SN] = btSNData.wedData;
    thuData[DocumentMapEnum.BT_SN] = btSNData.thuData;
    friData[DocumentMapEnum.BT_SN] = btSNData.friData;
    satData[DocumentMapEnum.BT_SN] = btSNData.satData;

    const clientSN = prepareDocumentDataByWeek(week, data[DocumentMapEnum.CLIENT_SN]);
    sunData[DocumentMapEnum.CLIENT_SN] = clientSN.sunData;
    monData[DocumentMapEnum.CLIENT_SN] = clientSN.monData;
    tueData[DocumentMapEnum.CLIENT_SN] = clientSN.tueData;
    wedData[DocumentMapEnum.CLIENT_SN] = clientSN.wedData;
    thuData[DocumentMapEnum.CLIENT_SN] = clientSN.thuData;
    friData[DocumentMapEnum.CLIENT_SN] = clientSN.friData;
    satData[DocumentMapEnum.CLIENT_SN] = clientSN.satData;

    const groupSN = prepareDocumentDataByWeek(week, data[DocumentMapEnum.GROUP_TRAINING]);
    sunData[DocumentMapEnum.GROUP_TRAINING] = groupSN.sunData;
    monData[DocumentMapEnum.GROUP_TRAINING] = groupSN.monData;
    tueData[DocumentMapEnum.GROUP_TRAINING] = groupSN.tueData;
    wedData[DocumentMapEnum.GROUP_TRAINING] = groupSN.wedData;
    thuData[DocumentMapEnum.GROUP_TRAINING] = groupSN.thuData;
    friData[DocumentMapEnum.GROUP_TRAINING] = groupSN.friData;
    satData[DocumentMapEnum.GROUP_TRAINING] = groupSN.satData;

    const parentSN = prepareDocumentDataByWeek(week, data[DocumentMapEnum.PARENT_SN]);
    sunData[DocumentMapEnum.PARENT_SN] = parentSN.sunData;
    monData[DocumentMapEnum.PARENT_SN] = parentSN.monData;
    tueData[DocumentMapEnum.PARENT_SN] = parentSN.tueData;
    wedData[DocumentMapEnum.PARENT_SN] = parentSN.wedData;
    thuData[DocumentMapEnum.PARENT_SN] = parentSN.thuData;
    friData[DocumentMapEnum.PARENT_SN] = parentSN.friData;
    satData[DocumentMapEnum.PARENT_SN] = parentSN.satData;

    const sessions = prepareDocumentDataByWeek(week, data[DocumentMapEnum.SESSION]);
    sunData[DocumentMapEnum.SESSION] = sessions.sunData;
    monData[DocumentMapEnum.SESSION] = sessions.monData;
    tueData[DocumentMapEnum.SESSION] = sessions.tueData;
    wedData[DocumentMapEnum.SESSION] = sessions.wedData;
    thuData[DocumentMapEnum.SESSION] = sessions.thuData;
    friData[DocumentMapEnum.SESSION] = sessions.friData;
    satData[DocumentMapEnum.SESSION] = sessions.satData;

    return [sunData, monData, tueData, wedData, thuData, friData, satData];
  }, [data, sun, mon, tue, wed, thu, fri, sat]);

  return (
    <Box>
      <CalendarBodyRow
        isFirstRow
        loading={loading}
        startHour={0}
        week={week}
        data={[sunData, monData, tueData, wedData, thuData, friData, satData]}
      />
      <CalendarBodyRow
        loading={loading}
        startHour={4}
        week={week}
        data={[sunData, monData, tueData, wedData, thuData, friData, satData]}
      />
      <CalendarBodyRow
        loading={loading}
        startHour={8}
        week={week}
        data={[sunData, monData, tueData, wedData, thuData, friData, satData]}
      />
      <CalendarBodyRow
        loading={loading}
        startHour={12}
        week={week}
        data={[sunData, monData, tueData, wedData, thuData, friData, satData]}
      />
      <CalendarBodyRow
        loading={loading}
        startHour={16}
        week={week}
        data={[sunData, monData, tueData, wedData, thuData, friData, satData]}
      />
      <CalendarBodyRow
        loading={loading}
        startHour={20}
        week={week}
        isLastRow
        data={[sunData, monData, tueData, wedData, thuData, friData, satData]}
      />
    </Box>
  );
}

export default CalendarBody;
